import React, { useRef } from 'react';
import { useAria } from '@/context/AriaContext';
import { AnimationType } from '@/types/aria';
import styles from './AvatarCore.module.css';

interface AvatarCoreProps {
  className?: string;
  size?: 'small' | 'medium' | 'large';
  isChatOpen?: boolean;
  state: AnimationType;
  format: 'video';
}

const AnimationPaths: Record<AnimationType, string> = {
  idle: '/aria/animations/idle.mp4',
  speaking: '/aria/animations/speaking.mp4',
  listening: '/aria/animations/listening.mp4',
  thinking: '/aria/animations/thinking.mp4'
};

const SizeConfig = {
  small: { width: '36px', height: '36px' },
  medium: { width: '48px', height: '48px' },
  large: { width: '64px', height: '64px' },
} as const;

export interface AvatarCoreHandle {
  handleSpeaking: () => Promise<void>;
  handleAnimation: (state: AnimationType) => void;
}

export const AvatarCore = React.forwardRef<AvatarCoreHandle, AvatarCoreProps>(({
  className = '',
  size = 'medium',
  isChatOpen = false
}, ref) => {
  const { state, dispatch } = useAria(); // Added dispatch to access the reducer
  const currentState = state.config.animation.currentState as AnimationType;
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    if (!containerRef.current) return;
    try {
      if (!videoRef.current) {
        const video = document.createElement('video');
        video.src = AnimationPaths[currentState];
        video.loop = true;
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        video.style.cssText = `
          width: 100%;
          height: ${isChatOpen ? 'calc(100% - 2rem)' : '100%'};
          object-fit: cover;
          border-radius: inherit;
          background-color: transparent;
          display: block;
          position: relative;
          z-index: 0;
        `;
        videoRef.current = video;
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(video);
      }

      if (videoRef.current) {
        videoRef.current.src = AnimationPaths[currentState];
        videoRef.current.play().catch(error => {
          console.error('Animation playback error:', error);
          // Fallback to idle state if animation fails
          if (currentState !== 'idle') {
            dispatch({ type: 'SET_ANIMATION', payload: 'idle' });
          }
        });
      }
    } catch (error) {
      console.error('Animation initialization error:', error);
    }
  }, [currentState, isChatOpen, dispatch]); // Added dispatch to dependencies

  const dimensions = SizeConfig[size];

  React.useImperativeHandle(ref, () => ({
    handleSpeaking: async () => {
      if (dispatch) {
        dispatch({ type: 'SET_ANIMATION', payload: 'speaking' });
      }
    },
    handleAnimation: (state: AnimationType) => {
      if (dispatch) {
        dispatch({ type: 'SET_ANIMATION', payload: state });
      }
    }
  }));

  return (
    <div
      ref={containerRef}
      className={`aria-avatar ${className}`}
      style={{
        minWidth: dimensions.width,
        minHeight: dimensions.height,
        position: 'relative',
      }}
      role="img"
      aria-label={`Aria AI Assistant - ${currentState} state`}
      data-testid="avatar-container"
      data-animation-state={currentState}
    />
  );
});

AvatarCore.displayName = 'AvatarCore';
export default AvatarCore;